<template>
  <div class="with-side-panel">
    <div class="content-i">
      <div class="content-box">
        <div class="element-wrapper">

          <BackButton
              :label="$t('general.button.back')"
              :to="{ name: 'regionList' }"
              marginBottom="5"/>

          <div class="d-flex justify-content-between">
            <h6 class="element-header">
              {{ $t('region.title') }}
            </h6>
          </div>

          <div class="element-box">
            <form @submit.prevent="submitForm" novalidate>
              <div class="row">
                <div class="col-lg-6 col-md-6 col-sm-12">
                  <BaseInput v-model="formData.name"
                             label="Bezeichnung"
                             :required=true
                             :validation="v$.name"
                             :messages="v$.name.required.$message"/>
                </div>
                <div class="col-lg-6 col-md-6 col-sm-12">
                  <SelectVue
                      v-model="formData.workingDays"
                      :options="workingDaysDropdown"
                      :is-clear="true"
                      :required="true"
                      :is-multi="true"
                      label="Arbeitstage"
                      placeholder="Arbeitstage"
                      :validation="v$.workingDays"
                      :messages="v$.workingDays.required.$message"/>
                </div>
              </div>
              <div class="form-buttons-w text-center">
                <SaveButton
                    :label="$t('general.button.save')"
                    :loading="isSubmitting"/>
              </div>
            </form>
          </div>

          <div class="element-box">

            <div class="d-flex justify-content-between align-items-center mb-3">
              <PerPage :values="[10, 25, 50, 100]" @update:perPage="getRegionHolidays"/>
              <button type="button" class="btn btn-primary" @click="handleHolidayFormModal">Urlaub erstellen</button>
            </div>

            <div class="table-responsive overflow-auto mt-3">
              <table class="table table-striped table-hover">
                <thead>
                <tr>
                  <th>Datum</th>
                  <th class="text-end">{{ $t('general.actions') }}</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="(holiday) in holidays.data" :key="holiday.id"
                    class="table-row"
                    @click="handleEditHolidayFormModal({
                                'id': holiday.id,
                                'date': holiday.date,
                                'publicHoliday': holiday.publicHoliday
                            })">
                  <td>{{ holiday?.date }}</td>
                  <td class="text-end">
                    <button class="btn btn-danger ms-2"
                            @click.stop="handleDeleteHolidayModal(holiday.id)">
                      <i class="os-icon os-icon-trash"></i>
                    </button>
                  </td>
                </tr>
                </tbody>
              </table>
            </div>

            <Pagination
                v-if="holidays?.pagination?.total_pages > 1"
                :totalPages="holidays?.pagination?.total_pages"
                :currentPage="holidays?.pagination?.current_page"
                :totalItems="holidays?.pagination?.total_items"
                :perPage="holidays?.pagination?.items_per_page"
                @pagechanged="getRegionHolidays"/>
          </div>
        </div>
      </div>
      <Sidebar></Sidebar>
    </div>
  </div>

  <ModalDelete
      v-model="showDeleteModal"
      :header="t('general.delete_item_header')"
      :onDelete="confirmDeleteHoliday">
  </ModalDelete>

  <Modal
      v-model="showHolidayModal"
      title="Urlaub"
      :hide-cancel="true"
      :button-label="$t('general.button.save')"
      sizeModal="modal-md"
      :hide-footer="true"
      classFooter="justify-content-center">
    <form @submit.prevent="submitHolidayForm" novalidate>
      <Datepicker
          v-model="holidayFormData.date"
          :required="true"
          label="Datum"
          position="bottom"
          :validation="vHoliday$.date"
          :messages="vHoliday$.date.required.$message"/>
      <BaseCheckbox
          v-model="holidayFormData.publicHoliday"
          label="Bundeseinheitlicher Feiertag"
          :labelPosition="'right'"/>
      <div class="d-flex justify-content-center w-100 mt-3">
        <SaveButton
            :label="$t('general.button.save')"
            :loading="isSubmitting"/>
      </div>
    </form>
  </Modal>

</template>

<script setup>
import regionService from '@/api/Region';
import BaseInput from '@/components/base/BaseInput.vue';
import CreateButton from '@/components/CreateButton.vue';
import Sidebar from '@/components/FormSidebar.vue';
import Modal from '@/components/modal/Modal.vue';
import ModalDelete from '@/components/modal/ModalDelete.vue';
import Pagination from '@/components/Pagination.vue';
import PerPage from '@/components/PerPage.vue';
import Datepicker from '@/components/picker/Datepicker.vue';
import SelectVue from '@/components/SelectVue.vue';
import {CloseToast, ErrorToast, SuccessToast} from '@/components/VueToast';
import router from '@/router';
import {requiredRule} from '@/shared/validators/validationRules';
import {piniaEnumsStore} from '@/store/piniaEnumsStore';
import useVuelidate from '@vuelidate/core';
import {computed, onMounted, ref} from 'vue';
import {useI18n} from 'vue-i18n';
import {useRoute} from 'vue-router';
import BaseCheckbox from "@/components/base/BaseCheckbox.vue";
import BackButton from "@/components/button/BackButton.vue";
import SaveButton from "@/components/button/SaveButton.vue";

const piniaEnumsState = piniaEnumsStore();
const getEnums = computed(() => piniaEnumsState.enums);
const {t} = useI18n();
const workingDaysDropdown = ref([]);
const isSubmitting = ref(false);
const route = useRoute();
const regionId = route.params.id;
const holidayId = ref(null);
const holidays = ref([]);
const showDeleteModal = ref(false);
const showHolidayModal = ref(false);

// REGION FORM
const formData = ref({
  name: '',
  workingDays: [],
});

const rules = computed(() => {
  return {
    name: requiredRule(t),
    workingDays: requiredRule(t),
  };
});

const v$ = useVuelidate(rules, formData);

async function submitForm() {
  v$.value.$touch();
  if (v$.value.$invalid) {
    return;
  }

  if (isSubmitting.value) return;
  isSubmitting.value = true;

  try {
    if (regionId) {
      await regionService.putRegion(formData.value, +regionId);
    } else {
      await regionService.postRegion(formData.value);
    }

    await router.push({name: 'regionList'});
    SuccessToast(t('general.toasts.save_success'));
    isSubmitting.value = false;
  } catch (error) {
    ErrorToast(error);
  } finally {
    isSubmitting.value = false;
  }
}

async function getRegion() {
  try {
    const {data} = await regionService.getRegion(+regionId);
    //formData.value = { ...data.data, officeId: data.data.office.id };
    formData.value = data.data;
  } catch (error) {
    await ErrorToast(error);
  }
}

async function getRegionHolidays(page = 1, perPage) {
  try {
    CloseToast();

    const perPageRes = holidays.value?.pagination?.items_per_page;

    const {data} = await regionService.getRegionHolidays(+regionId, {
      page: page,
      limit: perPage ?? perPageRes,
    });
    holidays.value = data;
  } catch (error) {
    await ErrorToast(error);
  }
}

// HOLIDAY FORM
const holidayFormData = ref({
  regionId: regionId,
  date: '',
  publicHoliday: false
});

const holidayRules = computed(() => {
  return {
    date: requiredRule(t),
  };
});

const vHoliday$ = useVuelidate(holidayRules, holidayFormData);

function handleHolidayFormModal() {
  CloseToast();
  holidayFormData.value.date = null;
  showHolidayModal.value = true;
  holidayId.value = null;
}

function handleEditHolidayFormModal(data) {
  CloseToast();
  holidayFormData.value.date = null;
  showHolidayModal.value = true;
  holidayId.value = data.id;
  holidayFormData.value.date = data.date
  holidayFormData.value.publicHoliday = data.publicHoliday
}

function handleDeleteHolidayModal(id) {
  CloseToast();
  showHolidayModal.value = false;
  showDeleteModal.value = true;
  holidayId.value = id;
}

async function confirmDeleteHoliday() {
  try {
    await regionService.deleteRegionHoliday(+holidayId.value);
    showDeleteModal.value = false;
    holidayId.value = null;
    await getRegionHolidays();
  } catch (error) {
    await ErrorToast(error);
  }
}

async function submitHolidayForm() {
  vHoliday$.value.$touch();
  if (vHoliday$.value.$invalid) {
    return;
  }

  if (isSubmitting.value) return;
  isSubmitting.value = true;

  try {
    if (holidayId.value) {
      await regionService.putRegionHoliday(holidayFormData.value, +holidayId.value);
    } else {
      await regionService.postRegionHoliday(holidayFormData.value);
    }

    showHolidayModal.value = false;
    await getRegionHolidays();
    SuccessToast(t('general.toasts.save_success'));
  } catch (error) {
    ErrorToast(error);
  } finally {
    isSubmitting.value = false;
  }
}

async function getWorkingDayEnumDropdown() {
  workingDaysDropdown.value = getEnums?.value?.daysInWeek || [];
}

onMounted(async () => {
  await getWorkingDayEnumDropdown();

  if (regionId) {
    await getRegion();
    await getRegionHolidays();
  }
});

</script>

<style scoped>
</style>